import { gql } from '@apollo/client';

// ==================== FRAGMENTS ====================

export const SOFTWARE_CONFIG_FIELDS = gql`
  fragment SoftwareConfigFields on OrganizationSoftwareConfig {
    id
    organizationId
    ssaEffectiveDate
    ssaExpirationDate
    notes
    createdAt
    updatedAt
  }
`;

export const SOFTWARE_SKU_FIELDS = gql`
  fragment SoftwareSkuFields on OrganizationSoftwareSku {
    id
    organizationId
    productId
    product {
      id
      sku
      name
      description
      unitPrice
      category
      isActive
    }
    overridePrice
    notes
    createdBy
    priceOverrideHistory {
      id
      price
      effectiveDate
      updatedBy
      createdAt
    }
    createdAt
    updatedAt
  }
`;

export const SERVICE_CONFIG_FIELDS = gql`
  fragment ServiceConfigFields on OrganizationServiceConfig {
    id
    organizationId
    servicesDiscountPercent
    psaEffectiveDate
    psaExpirationDate
    notes
    createdAt
    updatedAt
  }
`;

export const CUSTOM_SKU_FIELDS = gql`
  fragment CustomSkuFields on OrganizationCustomSku {
    id
    organizationId
    sku
    name
    description
    price
    unit
    isActive
    createdBy
    createdAt
    updatedAt
  }
`;

// ==================== QUERIES ====================

export const GET_ORGANIZATION_PRICING = gql`
  ${SOFTWARE_CONFIG_FIELDS}
  ${SOFTWARE_SKU_FIELDS}
  ${SERVICE_CONFIG_FIELDS}
  ${CUSTOM_SKU_FIELDS}
  query GetOrganizationPricing($organizationId: ID!) {
    organizationPricing(organizationId: $organizationId) {
      softwareConfig {
        ...SoftwareConfigFields
      }
      softwareSkus {
        ...SoftwareSkuFields
      }
      serviceConfig {
        ...ServiceConfigFields
      }
      customSkus {
        ...CustomSkuFields
      }
    }
  }
`;

// ==================== MUTATIONS ====================

export const UPSERT_SOFTWARE_CONFIG = gql`
  ${SOFTWARE_CONFIG_FIELDS}
  mutation UpsertSoftwareConfig($input: UpsertSoftwareConfigInput!) {
    upsertSoftwareConfig(input: $input) {
      ...SoftwareConfigFields
    }
  }
`;

export const ADD_SOFTWARE_SKU = gql`
  ${SOFTWARE_SKU_FIELDS}
  mutation AddSoftwareSku($input: AddSoftwareSkuInput!) {
    addSoftwareSku(input: $input) {
      ...SoftwareSkuFields
    }
  }
`;

export const UPDATE_SOFTWARE_SKU = gql`
  ${SOFTWARE_SKU_FIELDS}
  mutation UpdateSoftwareSku($id: ID!, $input: UpdateSoftwareSkuInput!) {
    updateSoftwareSku(id: $id, input: $input) {
      ...SoftwareSkuFields
    }
  }
`;

export const REMOVE_SOFTWARE_SKU = gql`
  mutation RemoveSoftwareSku($id: ID!) {
    removeSoftwareSku(id: $id)
  }
`;

export const UPSERT_SERVICE_CONFIG = gql`
  ${SERVICE_CONFIG_FIELDS}
  mutation UpsertServiceConfig($input: UpsertServiceConfigInput!) {
    upsertServiceConfig(input: $input) {
      ...ServiceConfigFields
    }
  }
`;

export const CREATE_CUSTOM_SKU = gql`
  ${CUSTOM_SKU_FIELDS}
  mutation CreateCustomSku($input: CreateCustomSkuInput!) {
    createCustomSku(input: $input) {
      ...CustomSkuFields
    }
  }
`;

export const UPDATE_CUSTOM_SKU = gql`
  ${CUSTOM_SKU_FIELDS}
  mutation UpdateCustomSku($id: ID!, $input: UpdateCustomSkuInput!) {
    updateCustomSku(id: $id, input: $input) {
      ...CustomSkuFields
    }
  }
`;

export const DELETE_CUSTOM_SKU = gql`
  mutation DeleteCustomSku($id: ID!) {
    deleteCustomSku(id: $id)
  }
`;

// ==================== TYPES ====================

export interface SoftwareSkuPriceHistory {
  id: string;
  price: number;
  effectiveDate: string;
  updatedBy: string;
  createdAt: string;
}

export interface SoftwareSkuProduct {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  unitPrice: number;
  category: 'SOFTWARE' | 'SERVICES';
  isActive: boolean;
}

export interface OrganizationSoftwareConfig {
  id: string;
  organizationId: string;
  ssaEffectiveDate: string | null;
  ssaExpirationDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationSoftwareSku {
  id: string;
  organizationId: string;
  productId: string;
  product: SoftwareSkuProduct;
  overridePrice: number | null;
  notes: string | null;
  createdBy: string;
  priceOverrideHistory: SoftwareSkuPriceHistory[];
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationServiceConfig {
  id: string;
  organizationId: string;
  servicesDiscountPercent: number;
  psaEffectiveDate: string | null;
  psaExpirationDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationCustomSku {
  id: string;
  organizationId: string;
  sku: string;
  name: string;
  description: string | null;
  price: number;
  unit: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationPricing {
  softwareConfig: OrganizationSoftwareConfig | null;
  softwareSkus: OrganizationSoftwareSku[];
  serviceConfig: OrganizationServiceConfig | null;
  customSkus: OrganizationCustomSku[];
}

export interface GetOrganizationPricingResponse {
  organizationPricing: OrganizationPricing;
}

export interface GetOrganizationPricingVariables {
  organizationId: string;
}

// Mutation input types
export interface UpsertSoftwareConfigInput {
  organizationId: string;
  ssaEffectiveDate?: string | null;
  ssaExpirationDate?: string | null;
  notes?: string | null;
}

export interface AddSoftwareSkuInput {
  organizationId: string;
  productId: string;
  overridePrice?: number | null;
  notes?: string;
}

export interface UpdateSoftwareSkuInput {
  overridePrice?: number | null;
  notes?: string;
}

export interface UpsertServiceConfigInput {
  organizationId: string;
  servicesDiscountPercent?: number;
  psaEffectiveDate?: string | null;
  psaExpirationDate?: string | null;
  notes?: string | null;
}

export interface CreateCustomSkuInput {
  organizationId: string;
  sku: string;
  name: string;
  description?: string;
  price: number;
  unit?: string;
}

export interface UpdateCustomSkuInput {
  sku?: string;
  name?: string;
  description?: string;
  price?: number;
  unit?: string;
  isActive?: boolean;
}
